import React from 'react'
import * as R from 'ramda'
import styled from 'styled-components'

import { EmbedIntegrationsDescription } from 'components/EmbedIntegrationsDescription'
import EmbedRatingIconSelectorForm from 'components/EmbedRatingIconSelectorForm'
import EmbedCodeRatingIconSizeForm from 'components/EmbedCodeRatingIconSizeForm'
import PlaceholderRemoverForm from 'components/PlaceholderRemoverForm'
import GenerateEmbedCodeModal from 'components/GenerateEmbedCodeModal'
import { getEmailPreviewIframeHeight, getIconSize, getLabelFontSize } from 'domains/survey'
import { EmailPreview } from 'components/EmailPreview/EmailPreview'
import { HelpdeskIntegration } from 'organisms/HelpdeskIntegration'
import { GenerateEmbedCodeForm } from 'organisms/GenerateEmbedCodeForm'
import { canUseOpaqueImage } from 'components/GenerateEmbedCodeModal/utils'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

const StyledContainer = styled.div`
  margin-top: 2rem;
  border-radius: 4px;
  background-color: #f6f6f6;
  padding: 1.625em !important;
`

/**
 *
 * @param {object} props
 * @param {object} props.primaryQuestion
 * @param {object} props.publishSettings
 * @param {string} props.selectedIntegration
 * @param {object} props.surveyConfiguration
 * @param {string} props.embedStyleType
 * @param {function} props.setEmbedStyleType
 * @param {boolean} props.showPlaceholderRemover
 * @param {boolean} props.isRemovePlaceholder
 * @param {function} props.setIsRemovePlaceholder
 * @param {object} [props.language]
 * @param {object} props.placeholders
 * @param {object} props.customField
 * @param {boolean=} props.hideEmojis
 * @param {function} [props.onUpdateGenericEmbedPlaceholdersAndCustomFields]
 */
export function STAREmbed({
  primaryQuestion,
  publishSettings,
  selectedIntegration,
  surveyConfiguration,
  embedStyleType,
  setEmbedStyleType,
  showPlaceholderRemover,
  isRemovePlaceholder,
  setIsRemovePlaceholder,
  placeholders,
  customField,
  hideEmojis,
  onUpdateGenericEmbedPlaceholdersAndCustomFields,
}) {
  const iconSizeLabel = publishSettings.ratingIconSize
  const isReverseOrder = publishSettings.choiceOrder
  const shouldShowLabel = publishSettings.isShowLabel
  const shouldHandleImagesNotLoaded = publishSettings.isDisplayColor
  const firstChoiceIcon = R.path(['choices', 0, 'image'], primaryQuestion)
  const shouldShowHandleImagesNotLoadedCheckbox = canUseOpaqueImage(firstChoiceIcon)

  const overrideEmbedStyleType = ['icon', 'emoji'].includes(embedStyleType)
    ? 'icon'
    : embedStyleType

  const ratingIconSize = getIconSize(overrideEmbedStyleType, iconSizeLabel)
  const padding = ratingIconSize === 90 ? 10 : 5
  const labelFontSize = getLabelFontSize(iconSizeLabel)
  const ratingIconDimension = {
    ratingIconSize,
    padding,
    labelFontSize,
  }
  const ratingIconDisplayOptions = {
    isReverseOrder,
    shouldHandleImagesNotLoaded,
    shouldShowLabel,
    isRemovePlaceholder,
  }
  const emailPreviewIframeHeight = getEmailPreviewIframeHeight(iconSizeLabel, shouldShowLabel)

  const generateEmbedCodeModalProps = {
    surveyToken: publishSettings.surveyToken,
    currentSelectedIntegration: selectedIntegration,
    description: surveyConfiguration.embedModal.content,
    linkText: surveyConfiguration.embedModal.linkText,
    linkUrl: surveyConfiguration.embedModal.linkUrl,
    isVisualToggleShown: surveyConfiguration.isShowVisual,
    primaryQuestion,
    ratingIconDimension,
    embedStyleType,
    ratingIconDisplayOptions,
    placeholders,
    emailPreviewIframeHeight,
    customField,
    onUpdateGenericEmbedPlaceholdersAndCustomFields,
  }

  return (
    <React.Fragment>
      <div className="grid ui stackable">
        <div className="seven wide column">
          <EmbedIntegrationsDescription
            label={surveyConfiguration.label}
            description={surveyConfiguration.overview}
          />
          <React.Fragment>
            <EmbedRatingIconSelectorForm
              className="mt4"
              embedStyleType={embedStyleType}
              setEmbedStyleType={setEmbedStyleType}
              shouldHandleImagesNotLoaded={shouldHandleImagesNotLoaded}
              setShouldHandleImagesNotLoaded={(value) => {
                publishSettings.handleChangeSetups('isDisplayColor', value)
              }}
              shouldShowLabel={shouldShowLabel}
              setShouldShowLabel={(value) => {
                publishSettings.handleChangeSetups('isShowLabel', value)
              }}
              shouldShowHandleImagesNotLoadedCheckbox={shouldShowHandleImagesNotLoadedCheckbox}
              hideEmojis={hideEmojis}
            />
            <EmbedCodeRatingIconSizeForm
              iconSizeLabel={iconSizeLabel}
              onChange={(value) => {
                publishSettings.handleChangeSetups('ratingIconSize', value)
              }}
            />
          </React.Fragment>
          {showPlaceholderRemover && (
            <PlaceholderRemoverForm
              isRemovePlaceholder={isRemovePlaceholder}
              onChange={setIsRemovePlaceholder}
            />
          )}
          <GenerateEmbedCodeModal {...generateEmbedCodeModalProps} className="mt4" />
        </div>
        <div className="nine wide column">
          <EmailPreview
            surveyToken={publishSettings.surveyToken}
            placeholders={placeholders}
            embedStyleType={embedStyleType}
            primaryQuestion={primaryQuestion}
            currentSelectedIntegration={selectedIntegration}
            ratingIconDisplayOptions={ratingIconDisplayOptions}
            ratingIconDimension={ratingIconDimension}
            emailPreviewIframeHeight={emailPreviewIframeHeight}
            customField={generateEmbedCodeModalProps.customField}
          />
        </div>
      </div>
      <HelpdeskIntegrationSection
        generateEmbedCodeModalProps={generateEmbedCodeModalProps}
        selectedIntegration={selectedIntegration}
        publishSettings={publishSettings}
      />
    </React.Fragment>
  )
}

function HelpdeskIntegrationSection({
  selectedIntegration,
  publishSettings,
  generateEmbedCodeModalProps,
}) {
  const { surveyId } = useParams()

  function mapState({ data: { surveys } }) {
    return {
      isUpdateTicketTagsAndCommentActive: R.path(
        ['settings', 'updateTicketTagsAndComment', 'isActive'],
        surveys.entity[surveyId]
      ),
    }
  }
  const { isUpdateTicketTagsAndCommentActive } = useSelector(mapState)

  const isZendeskAuthenticated =
    selectedIntegration === 'zendesk' && isUpdateTicketTagsAndCommentActive
  if (isZendeskAuthenticated) {
    return (
      <div className="grid ui stackable">
        <StyledContainer className="wide column">
          <HelpdeskIntegration
            selectedIntegration={selectedIntegration}
            surveyToken={publishSettings.surveyToken}
          />
        </StyledContainer>
      </div>
    )
  }

  if (selectedIntegration === 'generic_embed') {
    return (
      <div className="grid ui stackable">
        <StyledContainer className="wide column">
          <HelpdeskIntegration
            selectedIntegration={selectedIntegration}
            surveyToken={publishSettings.surveyToken}
          />
          <GenerateEmbedCodeForm
            {...publishSettings}
            handleChangePlaceHolders={publishSettings.handleChangePlaceHolders}
          />
          <GenerateEmbedCodeModal {...generateEmbedCodeModalProps} className="mt2" />
        </StyledContainer>
      </div>
    )
  }

  // Freshdesk and others
  return null
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/EmbedSurveyV2/STAREmbed.js